import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createEditorState, makeid, shortUUID } from "../../helpers";
import { ImportedContactFileInfoType } from "../../Broadcast/ImportContactsModal";
import { CommsSettingsDataType } from "./settingsSlice";

const EMPTY_ARRAY = Object.freeze([]) as never[];

export type BroadcastChannelType = "sms" | "email" | "voice";

export interface BroadcastHandlerType {
  CommsChannel?: ("sms" | "email" | "voice")[];
  Do: "auto-reply" | "forward" | "run-project";
  IsDeliveryStatus?: boolean;
  DeliveryStatusGroup?: ("success" | "sent" | "failed" | "expired")[];
  DeliveryStatusLetter?: string[];
  DynamicHeader?: string;
  UseNumber?: string;
  UseThread?: boolean;
  Keywords?: string[];
  ForwardMethod?: "sms" | "email";
  ForwardTo?: string;
  ProjectId?: number;
  Message: {
    EditorState: any;
  };
  EmailSubject?: {
    EditorState: any;
  };
  EmailFrom?: {
    EditorState: any;
  };
}
export interface BroadcastMessageType {
  Uuid?: string;
  Channel: BroadcastChannelType;
  DynamicHeader: string;
  Subject: string;
  EditDefaults: boolean;

  PlainText: string;
  Tokens: any[];
  Sender: string;

  RetryAttempts: number;
  RetryDelay: number;
  RetryUnit: string;

  IsRedirect: boolean;
  RedirectMessage: string;
  RedirectNumber: string;
  SelectComms: "onePerField" | "first";
  SelectCommsUuids: string[];
  Message: {
    Plain: string;
    PlainText?: string;
    Rendered: string;
    EditorState: any;
  };
  Shortlinks: {
    uuid: string;
    startingWith: string[];
    expiresAfterVisits: number;
    expireAfterDays: number;
    expires: string; // date only
    isProtected: boolean;
    password: string;
  };
  EmailSubject?: any;
  EmailFrom?: any;
  EmailSignature?: any;
  IsOpenTicket?: boolean;
}

export interface BroadcastType {
  Uuid?: string;
  FromUuid: string;
  Status?: "draft" | "published" | "archived";
  ClientID: number;
  Label: string;
  Description: string;
  Reference: string;
  Email: string;
  Messages: BroadcastMessageType[];
  CommsMethod: "one-way" | "two-way";
  ContactUuids: string[];
  Groups: string[];
  SendList: string;
  File?: string;
  IsFileImport: boolean;
  FileImportHeader?: string[];
  BuilderConfiguration?: { Tokens: any[] };
  Handlers?: BroadcastHandlerType[];
  TagsMap?: { category: string[]; industry: string[]; folder: string[] };
  refresh?: number; // used to trigger re-render when broadcast is updated from other places like draft
  Events?: string[];
  Assets?: string[];
}

const newMessage = (
  c: BroadcastChannelType,
  commsSettings: CommsSettingsDataType
): BroadcastMessageType => ({
  Channel: c,
  Uuid: shortUUID(),
  DynamicHeader: "",
  EditDefaults: false,
  IsRedirect: false,
  PlainText: "",
  RedirectMessage: "",
  RedirectNumber: "",
  RetryAttempts: 1,
  RetryDelay: 5,
  RetryUnit: "minutes",
  Sender: "",
  Subject: "",
  Tokens: [],
  SelectComms: "first", // or onePerField
  SelectCommsUuids: [],
  Message: {
    PlainText: "",
    Plain: "",
    Rendered: "",
    EditorState: createEditorState(),
  },
  Shortlinks: {
    uuid: "",
    startingWith: [],
    expiresAfterVisits: 0,
    expireAfterDays: 0,
    expires: "",
    isProtected: false,
    password: "",
  },
  ...(c === "sms"
    ? {
        IsOpenTicket:
          commsSettings.BroadcastSetting === "open-ticket" ? true : false,
      }
    : {}),
  ...(c === "voice"
    ? {
        WelcomeMessage: commsSettings.VoiceIntro || "",
        ExitMessage: commsSettings.VoiceExit || "",
        RetryAttempts: commsSettings.VoiceRetries || 1,
      }
    : {}),
  ...(c === "email"
    ? {
        EmailFrom: { EditorState: createEditorState(commsSettings.EmailFrom) },
        EmailSubject: {
          EditorState: createEditorState(commsSettings.EmailSubject),
        },
        EmailSignature: {
          EditorState: createEditorState(commsSettings.EmailSignature),
        },
      }
    : {}),
});

const newBroadcast = (commsSettings: CommsSettingsDataType): BroadcastType => ({
  // Uuid: shortUUID(),
  FromUuid: "",
  ClientID: 1,
  Status: "draft",
  Label: "New broadcast - " + new Date().toLocaleString(),
  Description: "New broadcast",
  Reference: makeid(8),
  Email: "developers@boomcomms.com",
  Messages: [newMessage("sms", commsSettings)],

  CommsMethod:
    commsSettings.BroadcastSetting === "dynamic-header" ? "one-way" : "two-way",
  ContactUuids: [],
  Groups: [],
  SendList: "",
  IsFileImport: false,
  Handlers: [],
  TagsMap: { category: [], industry: [], folder: [] },
});

const initialState: {
  broadcast: BroadcastType | null;
  currentChannel: BroadcastChannelType;
  importedContacts: any[];
  usedFile: ImportedContactFileInfoType | null;
} = {
  broadcast: null,
  currentChannel: "sms",
  usedFile: null,
  importedContacts: [],
};

// eslint-disable-next-line import/prefer-default-export
export const broadcastSlice = createSlice({
  name: "broadcast",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setBroadcast: (state, action) => {
      state.broadcast = action.payload;
    },
    setNewBroadcast: (state, action) => {
      const { commsSettings, ...payload } = action.payload;
      state.broadcast = {
        ...newBroadcast(commsSettings),
        ...(payload || {}),
      };
    },
    addNewMessage: (
      state,
      action: PayloadAction<{
        commsSettings: CommsSettingsDataType;
        channel: BroadcastChannelType;
      }>
    ) => {
      const { commsSettings, channel } = action.payload;
      state.broadcast?.Messages.push(newMessage(channel, commsSettings));
      state.currentChannel = channel;
    },
    removeMessage: (state, action: PayloadAction<BroadcastChannelType>) => {
      if (state.broadcast?.Messages) {
        state.broadcast.Messages = state.broadcast?.Messages.filter(
          (m) => m.Channel !== action.payload
        );
        state.currentChannel = state.broadcast.Messages?.[0]?.Channel;
      }
    },
    setCurrentBroadcastChannel: (
      state,
      action: PayloadAction<BroadcastChannelType>
    ) => {
      state.currentChannel = action.payload;
    },
    setimportedContacts: (state, action) => {
      state.importedContacts = action.payload;
    },
    setUsedFile: (state, action) => {
      state.usedFile = action.payload;
    },
    setStatusHandlers: (state, action) => {
      if (state.broadcast) {
        state.broadcast.Handlers = [
          ...(state.broadcast.Handlers?.filter((h) => !h.IsDeliveryStatus) ??
            []),
          ...action.payload,
        ];
      }
    },
    setReplyHandlers: (state, action) => {
      if (state.broadcast) {
        state.broadcast.Handlers = [
          ...(state.broadcast.Handlers?.filter((h) => h.IsDeliveryStatus) ??
            []),
          ...action.payload,
        ];
      }
    },
    addReplyHandler: (state, action) => {
      if (state.broadcast) {
        if (!state.broadcast.Handlers) state.broadcast.Handlers = [];
        state.broadcast.Handlers.push({
          Do: "forward",
          ForwardTo: "",
          ForwardMethod: "sms", // "email"
          Message: { EditorState: createEditorState() },
        });
      }
    },
    addStatusHandler: (state, action) => {
      if (state.broadcast) {
        if (!state.broadcast.Handlers) state.broadcast.Handlers = [];
        state.broadcast.Handlers.push({
          IsDeliveryStatus: true,
          Do: "forward",
          ForwardTo: "",
          ForwardMethod: "sms", // "email"
          Message: { EditorState: createEditorState() },
          DeliveryStatusGroup: ["sent", "failed"],
          DeliveryStatusLetter: ["S"],
        });
      }
    },
  },
});

export const {
  setBroadcast,
  setNewBroadcast,
  addNewMessage,
  removeMessage,
  setCurrentBroadcastChannel,
  setimportedContacts,
  setUsedFile,
  setStatusHandlers,
  setReplyHandlers,
  addReplyHandler,
  addStatusHandler,
} = broadcastSlice.actions;

export default broadcastSlice.reducer;

export const selectBroadcast = (state: {
  broadcast: { broadcast: BroadcastType };
}) => state.broadcast.broadcast;

export const selectCurrentBroadcastChannel = (state: {
  broadcast: { currentChannel: BroadcastChannelType };
}) => state.broadcast.currentChannel;

const selectHandlers = (state: { broadcast: { broadcast: BroadcastType } }) =>
  state.broadcast.broadcast.Handlers;

export const selectBroadcastReplyHandlers = createSelector(
  [selectHandlers],
  (handlers) => handlers?.filter((h) => !h.IsDeliveryStatus) ?? EMPTY_ARRAY
);

export const selectBroadcastStatusHandlers = createSelector(
  [selectHandlers],
  (handlers) => handlers?.filter((h) => h.IsDeliveryStatus) ?? EMPTY_ARRAY
);

export const selectimportedContacts = (state: {
  broadcast: { importedContacts: any };
}) => state.broadcast.importedContacts;

export const selectimportedFile = (state: {
  broadcast: { usedFile: ImportedContactFileInfoType };
}): ImportedContactFileInfoType => state.broadcast.usedFile;

export const selectimportedContactFields = (state: {
  broadcast: { usedFile: ImportedContactFileInfoType };
}) => state.broadcast.usedFile?.header || EMPTY_ARRAY;

export const selectIsOpenTicket = (state: {
  broadcast: { broadcast: { Messages: any[] } };
}) =>
  state.broadcast.broadcast?.Messages?.find((m) => m.Channel === "sms")
    ?.IsOpenTicket;
